import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as CloudBedsIcon } from "../../assets/icons/cloud-beds.svg";
import { ReactComponent as DotsIcon } from "../../assets/icons/dots.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/email.svg";
import { ReactComponent as MewsIcon } from "../../assets/icons/mews.svg";
import Button from "../../components/Button";
import { Page } from "../../components/Page";
import UIPopover from "../../components/Popover";
import { Search } from "../../components/Search";
import { plans } from "../../constant";
import { api } from "../../helpers/api";
import { BlockedUsers } from "./BlockedUsers";
import { ConfirmBlock } from "./components/DeactivateModal";
import { ConfirmDelete } from "./components/DeleteUserModal";
import { UserDetails } from "./users-details/UserDetails";

// export function Users() {
//   return (
//     <Page>
//       <div
//         className={`p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1 1xl:w-[unset]`}
//       >
//         <div className="flex items-center justify-between">
//           <h1 className="text-2xl font-semibold text-black">Manage users</h1>
//         </div>
//       </div>
//     </Page>
//   );
// }

type Person = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  joinedOn: string;
  tier: string;
  pms: string;
  lastActive: string;
  profileImg: string;
  isActive: boolean;
  isInLinkedGroup?: boolean;
};

const columnHelper = createColumnHelper<Person>();

export function Users() {
  const [sortBy, setSortBy] = useState<"name" | undefined>();
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | undefined>();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<Person | null>(null);
  const [search, setSearch] = React.useState("");
  const [tableData, setTableData] = React.useState<Person[]>([]);
  const [isBlockedUsers, setIsBlockedUsers] = React.useState(false);
  const loadMoreRef = useRef<any>(null);
  const [filters, setFilters] = useState<{
    date: { startDate?: string; endDate?: string };
    types?: string[];
    selectedDateFilter?: string;
  }>();
  const [tempFilters, setTempFilters] = React.useState<{
    date: { startDate: string; endDate: string };
    types: string[];
    selectedDateFilter: string;
  }>({
    date: { startDate: "", endDate: "" },
    types: [],
    selectedDateFilter: "",
  });
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] =
    useState<any>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<any>(false);
  const queryClient = useQueryClient();

  const fetchUsers = async ({ pageParam }) => {
    const res = await api.getUsers({
      page: pageParam,
      limit: 10,
      searchKeyword: search,
      filters,
      sortBy,
      sortOrder,
    });
    return res;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["users", search, filters, sortBy, sortOrder],
    queryFn: fetchUsers,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages, pageParam) => {
      // console.log(lastPage);
      if (lastPage.length < 10) return null;
      return pageParam + 1;
    },
  });

  // queryKey: ["users", search, filters, sortBy, sortOrder],
  //   queryFn: fetchUsers,
  //   initialPageParam: 0,
  //   getNextPageParam: (lastPage, pages, pageParam) => {
  //     // console.log(lastPage);
  //     if (lastPage.length < 10) return null;
  //     return pageParam + 1;
  //   },

  const columns = [
    columnHelper.accessor("firstName", {
      header: ({ column }) => {
        return (
          <button
            onClick={() => {
              if (sortBy === "name") {
                setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
              } else {
                setSortBy("name");
                setSortOrder("ASC");
              }
            }}
            className="p-0 flex items-center gap-[2px]"
          >
            Name
            {sortBy === "name" ? (
              sortOrder === "ASC" ? (
                <ArrowDownIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 rotate-[180deg]" />
              )
            ) : null}
          </button>
        );
      },
      cell: (info) => (
        <div className="flex flex-row items-center gap-2.5">
          <span>
            {info.row.original.firstName} {info.row.original.lastName}
          </span>
          {info.row.original.isInLinkedGroup && (
            <span
              data-testid={`linked-badge-${info.row.original.id}`}
              title="Part of a linked account group"
              className="text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border border-border-primary bg-surface-secondary text-content-secondary"
            >
              Linked
            </span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.email, {
      header: "Email",
      cell: (info) => (
        <div className="flex flex-row items-center gap-1">
          <EmailIcon className="w-5 h-5" />
          <span>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("tier", {
      header: "Plan type",
      cell: (info) =>
        !info.getValue() ? (
          <div>-</div>
        ) : (
          <div
            className={`${
              plans.find((plan) => plan.type === info.getValue())?.planStyle
            } text-xs font-medium leading-3 border rounded py-1 px-1.5 flex items-center justify-center w-fit`}
          >
            {plans.find((plan) => plan.type === info.getValue())?.planName}
          </div>
        ),
    }),
    columnHelper.accessor("pms", {
      header: "PMS",
      cell: (info) => (
        <>
          {info.getValue() === "CLOUDBEDS" ? (
            <CloudBedsIcon className="h-[15px]" />
          ) : info.getValue() === "MEWS" ? (
            <MewsIcon className="h-[15px]" />
          ) : (
            "-"
          )}
        </>
      ),
    }),
    columnHelper.accessor("joinedOn", {
      header: "Joined on",
      cell: (info) => (
        <div className="flex flex-row justify-between w-full items-center">
          {info.getValue()
            ? moment(info.getValue()).format("MMM DD, YYYY") || "-"
            : "-"}
          <UIPopover
            trigger={
              <button className="!rounded-md h-[2.5rem] w-[2.5rem] flex !p-2 items-center justify-center [&_circle]:fill-black [&_circle]:hover:fill-white">
                <DotsIcon />
              </button>
            }
          >
            {(close) => (
              <div className="flex flex-col text-xs text-content-primary font-medium gap-4 bg-surface-primary p-4 rounded-lg shadow-dropdown font-inter mr-16">
                <button
                  className="text-start"
                  onClick={() => {
                    setSelectedUser(info.row.original);
                    setIsUserDetailsOpen(true);
                  }}
                >
                  View Details
                </button>
                <button
                  className="text-start"
                  onClick={() => {
                    setIsDeactivateModalOpen(info.row.original.id);
                  }}
                >
                  De-activate user
                </button>
                <button
                  className="text-[#F54343] text-start"
                  onClick={() => {
                    setIsDeleteModalOpen(info.row.original.id);
                  }}
                >
                  Delete user
                </button>
              </div>
            )}
          </UIPopover>
        </div>
      ),
    }),
  ];

  const table = useReactTable<Person>({
    data: tableData || [], // Correctly set the data property
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  useMemo(() => {
    if (data) {
      // Combine pages into one array
      const newData = data.pages.flatMap((page) => page);
      // Set the combined data into the table data state
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 1.0,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const hasFilters = useMemo(() => {
    return filters?.date.startDate || (filters?.types?.length || 0) > 0;
  }, [filters]);

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-surface-tertiary flex-1 1xl:w-[unset]">
        {selectedUser?.id && (
          <UserDetails
            isOpen={isUserDetailsOpen}
            handleClose={() => setIsUserDetailsOpen(false)}
            userId={selectedUser.id}
            userData={selectedUser}
            onDeactivate={() => setIsDeactivateModalOpen(selectedUser?.id)}
            onDelete={() => setIsDeleteModalOpen(selectedUser?.id)}
          />
        )}
        <BlockedUsers
          isOpen={isBlockedUsers}
          handleClose={() => setIsBlockedUsers(false)}
          setSelectedUser={setSelectedUser}
          setIsUserDetailsOpen={setIsUserDetailsOpen}
          refetchActiveUsers={refetch}
          onDelete={(user) => setIsDeleteModalOpen(user?.id)}
        />
        {/* {isDeleteModalOpen && selectedUser && (
        <ConfirmDelete
          onClose={() => setIsDeleteModalOpen(false)}
          userId={selectedUser.id}
          refetch={() => {
            refetch();
          }}
        />
      )}
      {isBlockModalOpen && selectedUser && (
        <ConfirmBlock
          onClose={() => setIsBlockModalOpen(false)}
          userId={selectedUser.id}
          refetch={() => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
          }}
          isActive={true}
        />
      )} */}

        <div className="flex flex-row justify-between">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-content-primary">
              Manage users
            </h1>
          </div>
        </div>
        <div className="mt-5 px-4 bg-surface-primary rounded-[10px] shadow-customCard border border-border-secondary">
          <div className="flex flex-row my-4 justify-between items-center">
            <Search
              onChange={(value) => {
                setSearch(value);
              }}
              value={search}
            />
            <Button
              variant="link"
              onClick={() => setIsBlockedUsers(!isBlockedUsers)}
              className="text-primary hover:text-primary-hover text-[13px] font-medium tracking-[-0.13px]"
            >
              Blocked Users
            </Button>
          </div>

          <div
            className={`overflow-auto ${
              hasFilters ? "h-[calc(100vh-260px)]" : "h-[calc(100vh-190px)]"
            } relative flex flex-col`}
          >
            <table className="w-full relative">
              <colgroup>
                <col style={{ width: "27%" }} />
                <col style={{ width: "23%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>
              <thead className="w-full">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="sticky top-0">
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={clsx(
                          "text-[0.875rem] font-medium text-content-primary text-start p-0",
                        )}
                      >
                        <div
                          className={clsx(
                            "w-full px-5 py-3 bg-surface-secondary flex flex-row items-center gap-1 transition-all",
                            index === headerGroup.headers.length - 1 &&
                              "border-r rounded-tr-[8px] rounded-br-[8px]",
                            index === 0 &&
                              "border-l rounded-tl-[8px] rounded-bl-[8px]",
                          )}
                          // onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                          {/* {{
                          asc: <ArrowIcon />,
                          desc: <ArrowIcon className="rotate-[180deg]" />,
                        }[header.column.getIsSorted() as string] ?? null} */}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {!isLoading &&
                  table.getRowModel().rows.map((row, index) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={clsx(
                            "px-5 py-1 border-b border-b-border-primary font-medium text-[0.8125rem]",
                            // last index no border
                            index === table.getRowModel().rows.length - 1 &&
                              "border-b-0",
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}

                {isFetching && (
                  <TableSkeleton
                    rows={5}
                    columns={table.getAllColumns().length}
                  />
                )}
              </tbody>
            </table>
            {!isLoading && !isFetching && tableData.length === 0 && (
              <div className="flex-1 flex justify-center items-center">
                <span className="text-content-tertiary text-[0.875rem] font-medium">
                  No data available
                </span>
              </div>
            )}
            <div className="">
              <button
                onClick={() => fetchNextPage()}
                disabled={!hasNextPage || isFetchingNextPage}
                ref={loadMoreRef}
              >
                {/* {isFetchingNextPage
                ? "Loading more..."
                : hasNextPage
                ? "Load More"
                : "Nothing more to load"} */}
              </button>
            </div>
            {/* <div>{isFetching && !isFetchingNextPage ? "Fetching..." : null}</div> */}
          </div>
        </div>
      </div>

      {isDeactivateModalOpen && (
        <ConfirmBlock
          onClose={() => setIsDeactivateModalOpen(false)}
          userId={isDeactivateModalOpen}
          refetch={() => {
            setSelectedUser(null);
            queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
            refetch();
          }}
          isActive={true}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDelete
          onClose={() => setIsDeleteModalOpen(false)}
          userId={isDeleteModalOpen}
          refetch={() => {
            setSelectedUser(null);
            queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
            refetch();
          }}
        />
      )}
    </Page>
  );
}

export function TableSkeleton({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-5 py-4 border-b border-b-border-primary font-medium text-[0.8125rem]"
            >
              <div className="w-full h-4 bg-surface-disabled rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
